// Localização: frontend/src/components/Prontuario/SearchBar.jsx (Versão 1.0.0)
// Componente de busca avançada para o prontuário do paciente

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Search, X, Clock, Loader2, ArrowUp, ArrowDown } from 'lucide-react';
import { searchProntuario } from '../../api/api';
import Alert from '../UI/Alert';

/**
 * Componente de busca com resultados em tempo real
 * @param {number} pacienteId - ID do paciente
 * @param {function} onResultSelect - Callback quando um resultado é selecionado
 * @param {string} placeholder - Texto placeholder
 * @param {object} filters - Filtros adicionais (type, startDate, endDate, usuarioId)
 * @param {boolean} compact - Se true, usa tamanho compacto (padrão do header)
 */
const SearchBar = ({ 
    pacienteId, 
    onResultSelect,
    placeholder = 'Buscar no prontuário...',
    filters = {},
    compact = false
}) => {
    const [searchQuery, setSearchQuery] = useState('');
    const [results, setResults] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [isOpen, setIsOpen] = useState(false);
    const [selectedIndex, setSelectedIndex] = useState(-1);
    const [error, setError] = useState(null);
    const [searchHistory, setSearchHistory] = useState([]);
    
    const inputRef = useRef(null);
    const resultsRef = useRef(null);
    const debounceTimeoutRef = useRef(null);
    const abortControllerRef = useRef(null);
    const cacheRef = useRef(new Map()); // Cache simples de resultados
    const isSearchingRef = useRef(false); // Flag para rastrear se há busca em andamento
    const lastSearchQueryRef = useRef(''); // Última query buscada
    const lastSearchTimeRef = useRef(0); // Timestamp da última busca
    const filtersRef = useRef(filters); // Manter referência estável dos filters
    const MIN_SEARCH_LENGTH = 2; // Mínimo de 2 caracteres para buscar
    const DEBOUNCE_DELAY = 600; // Aumentado para 600ms

    // Carregar histórico do localStorage
    useEffect(() => {
        const saved = localStorage.getItem(`prontuario_search_history_${pacienteId}`);
        if (saved) {
            try {
                setSearchHistory(JSON.parse(saved));
            } catch (e) {
                console.error('Erro ao carregar histórico de buscas:', e);
            }
        }
    }, [pacienteId]);

    // Atualizar filtersRef quando filters mudar
    useEffect(() => {
        filtersRef.current = filters;
    }, [filters]);

    // Salvar histórico no localStorage - usando ref para manter estável
    const saveToHistoryRef = useRef((query) => {
        if (!query || query.trim().length === 0) return;
        
        setSearchHistory(prev => {
            const updated = [
                query,
                ...prev.filter(item => item !== query)
            ].slice(0, 10); // Máximo de 10 itens
            
            localStorage.setItem(`prontuario_search_history_${pacienteId}`, JSON.stringify(updated));
            return updated;
        });
    });

    // Atualizar a referência quando pacienteId mudar
    useEffect(() => {
        saveToHistoryRef.current = (query) => {
            if (!query || query.trim().length === 0) return;
            
            setSearchHistory(prev => {
                const updated = [
                    query,
                    ...prev.filter(item => item !== query)
                ].slice(0, 10);
                
                localStorage.setItem(`prontuario_search_history_${pacienteId}`, JSON.stringify(updated));
                return updated;
            });
        };
    }, [pacienteId]);

    // Função de busca com cancelamento e cache - usando useRef para evitar recriações
    const performSearchRef = useRef(async (query) => {
        const trimmedQuery = query.trim();
        
        // Validação: mínimo de caracteres
        if (trimmedQuery.length < MIN_SEARCH_LENGTH) {
            setResults(null);
            setIsOpen(false);
            setIsLoading(false);
            isSearchingRef.current = false;
            lastSearchQueryRef.current = '';
            return;
        }

        // Proteção contra requisições duplicadas: verificar se já está buscando a mesma query
        const now = Date.now();
        const timeSinceLastSearch = now - lastSearchTimeRef.current;
        
        if (isSearchingRef.current && lastSearchQueryRef.current === trimmedQuery) {
            // Já está buscando a mesma query, ignorar
            return;
        }

        // Proteção adicional: se a mesma query foi buscada há menos de 500ms, ignorar
        if (lastSearchQueryRef.current === trimmedQuery && timeSinceLastSearch < 500) {
            return;
        }

        // Verificar cache
        const currentFilters = filtersRef.current;
        const cacheKey = `${pacienteId}_${trimmedQuery}_${JSON.stringify(currentFilters)}`;
        if (cacheRef.current.has(cacheKey)) {
            const cachedResult = cacheRef.current.get(cacheKey);
            setResults(cachedResult);
            setIsOpen(true);
            setIsLoading(false);
            isSearchingRef.current = false;
            return;
        }

        // Marcar como buscando
        isSearchingRef.current = true;
        lastSearchQueryRef.current = trimmedQuery;
        lastSearchTimeRef.current = now;

        // Cancelar requisição anterior se existir
        if (abortControllerRef.current) {
            abortControllerRef.current.abort();
        }

        // Criar novo AbortController
        abortControllerRef.current = new AbortController();
        const signal = abortControllerRef.current.signal;

        setIsLoading(true);
        setError(null);

        try {
            const response = await searchProntuario(pacienteId, trimmedQuery, currentFilters, signal);
            
            // Verificar se a requisição foi cancelada
            if (signal.aborted) {
                isSearchingRef.current = false;
                return;
            }
            
            if (response.data.success) {
                // Salvar no cache (máximo de 50 entradas)
                if (cacheRef.current.size >= 50) {
                    const firstKey = cacheRef.current.keys().next().value;
                    cacheRef.current.delete(firstKey);
                }
                cacheRef.current.set(cacheKey, response.data.data);
                
                setResults(response.data.data);
                setIsOpen(true);
                saveToHistoryRef.current(trimmedQuery);
            } else {
                setError(response.data.message || 'Erro ao realizar busca');
            }
        } catch (err) {
            // Ignorar erros de cancelamento
            if (err.name === 'AbortError' || signal.aborted) {
                isSearchingRef.current = false;
                return;
            }
            
            console.error('Erro na busca:', err);
            setError(err.response?.data?.message || 'Erro ao conectar com o servidor');
            setResults(null);
        } finally {
            if (!signal.aborted) {
                setIsLoading(false);
                isSearchingRef.current = false;
            }
        }
    });

    // Wrapper estável para performSearch
    const performSearch = useCallback((query) => {
        return performSearchRef.current(query);
    }, []);

    // Debounce da busca (600ms) com validação mínima
    useEffect(() => {
        // Limpar timeout anterior
        if (debounceTimeoutRef.current) {
            clearTimeout(debounceTimeoutRef.current);
        }

        // Cancelar requisição em andamento se houver
        if (abortControllerRef.current) {
            abortControllerRef.current.abort();
            isSearchingRef.current = false;
        }

        const trimmedQuery = searchQuery.trim();

        if (trimmedQuery.length >= MIN_SEARCH_LENGTH) {
            // Verificar se já está buscando a mesma query
            if (isSearchingRef.current && lastSearchQueryRef.current === trimmedQuery) {
                return; // Já está buscando, não agendar nova busca
            }

            debounceTimeoutRef.current = setTimeout(() => {
                // Verificar novamente antes de executar (pode ter sido cancelado)
                if (!isSearchingRef.current || lastSearchQueryRef.current !== trimmedQuery) {
                    performSearch(trimmedQuery);
                }
            }, DEBOUNCE_DELAY);
        } else if (trimmedQuery.length === 0) {
            // Limpar resultados se campo estiver vazio
            setResults(null);
            setIsOpen(false);
            setIsLoading(false);
            isSearchingRef.current = false;
            lastSearchQueryRef.current = '';
            lastSearchTimeRef.current = 0;
        } else {
            // Query muito curta - apenas mostrar loading sem buscar
            setIsLoading(false);
            setResults(null);
            setIsOpen(false);
            isSearchingRef.current = false;
        }

        return () => {
            if (debounceTimeoutRef.current) {
                clearTimeout(debounceTimeoutRef.current);
            }
        };
    }, [searchQuery, performSearch]);

    // Fechar resultados ao clicar fora
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (
                resultsRef.current &&
                !resultsRef.current.contains(event.target) &&
                inputRef.current &&
                !inputRef.current.contains(event.target)
            ) {
                setIsOpen(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    // Highlight de termos encontrados
    const highlightText = (text, query) => {
        if (!text || !query) return text;
        
        const parts = text.split(new RegExp(`(${query})`, 'gi'));
        return parts.map((part, index) => 
            part.toLowerCase() === query.toLowerCase() ? (
                <mark key={index} className="bg-yellow-200 font-semibold">{part}</mark>
            ) : (
                part
            )
        );
    };

    // Navegação por teclado
    const handleKeyDown = (e) => {
        if (!isOpen || !results) return;

        const allResults = [
            ...(results.paciente || []),
            ...(results.registrosDiario || []),
            ...(results.medicacoes || []),
            ...(results.equipamentos || []),
            ...(results.tarefas || [])
        ];

        switch (e.key) {
            case 'ArrowDown':
                e.preventDefault();
                setSelectedIndex(prev => 
                    prev < allResults.length - 1 ? prev + 1 : prev
                );
                break;
            case 'ArrowUp':
                e.preventDefault();
                setSelectedIndex(prev => prev > 0 ? prev - 1 : -1);
                break;
            case 'Enter':
                e.preventDefault();
                if (selectedIndex >= 0 && selectedIndex < allResults.length) {
                    handleResultClick(allResults[selectedIndex]);
                }
                break;
            case 'Escape':
                e.preventDefault();
                setIsOpen(false);
                setSelectedIndex(-1);
                break;
        }
    };

    // Scroll para item selecionado
    useEffect(() => {
        if (selectedIndex >= 0 && resultsRef.current) {
            const selectedElement = resultsRef.current.querySelector(
                `[data-result-index="${selectedIndex}"]`
            );
            if (selectedElement) {
                selectedElement.scrollIntoView({ block: 'nearest' });
            }
        }
    }, [selectedIndex]);

    // Limpar busca
    const handleClear = () => {
        setSearchQuery('');
        setResults(null);
        setIsOpen(false);
        setSelectedIndex(-1);
        setError(null);
        if (inputRef.current) {
            inputRef.current.focus();
        }
    };

    // Selecionar resultado
    const handleResultClick = (result) => {
        if (onResultSelect) {
            onResultSelect(result);
        }
        setIsOpen(false);
        setSelectedIndex(-1);
    };

    // Selecionar do histórico
    const handleHistoryClick = (query) => {
        setSearchQuery(query);
        performSearch(query);
        inputRef.current?.focus();
    };

    // Agrupar resultados por tipo
    const groupedResults = results ? {
        paciente: results.paciente || [],
        registrosDiario: results.registrosDiario || [],
        medicacoes: results.medicacoes || [],
        equipamentos: results.equipamentos || [],
        tarefas: results.tarefas || []
    } : null;

    // Contar total de resultados
    const totalResults = results ? results.total || 0 : 0;
    const allResultsFlat = groupedResults ? [
        ...groupedResults.paciente,
        ...groupedResults.registrosDiario,
        ...groupedResults.medicacoes,
        ...groupedResults.equipamentos,
        ...groupedResults.tarefas
    ] : [];

    return (
        <div className="relative w-full">
            {/* Input de Busca */}
            <div className="relative">
                <Search 
                    className={`absolute ${compact ? 'left-2 sm:left-3' : 'left-3'} top-1/2 transform -translate-y-1/2 ${compact ? 'h-4 w-4' : 'h-5 w-5'} text-gray-400`}
                />
                <input
                    ref={inputRef}
                    type="text"
                    value={searchQuery}
                    onChange={(e) => {
                        setSearchQuery(e.target.value);
                        setSelectedIndex(-1);
                    }}
                    onKeyDown={handleKeyDown}
                    onFocus={() => {
                        if (results) setIsOpen(true);
                    }}
                    placeholder={placeholder}
                    className={`
                        w-full ${compact ? 'py-1.5 sm:py-2' : 'py-2.5'} ${compact ? 'pl-8 sm:pl-10' : 'pl-10'} pr-10
                        ${compact ? 'bg-gray-50' : 'bg-white'} border border-gray-300 rounded-lg
                        ${compact ? '' : 'shadow-sm'}
                        focus:outline-none focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20 ${compact ? 'focus:bg-white' : ''}
                        transition-all duration-200
                        ${compact ? 'text-xs sm:text-sm' : 'text-sm'} text-gray-700 placeholder-gray-400
                        hover:border-gray-400 ${compact ? 'hover:bg-white' : ''}
                    `}
                    aria-label="Buscar no prontuário"
                    autoComplete="off"
                    minLength={MIN_SEARCH_LENGTH}
                />
                
                {/* Botão Limpar / Loading */}
                <div className="absolute right-2 top-1/2 transform -translate-y-1/2 flex items-center gap-1">
                    {isLoading && (
                        <Loader2 className="h-4 w-4 text-emerald-500 animate-spin" />
                    )}
                    {searchQuery && !isLoading && (
                        <button
                            onClick={handleClear}
                            className="p-1 hover:bg-gray-100 rounded-full transition-colors"
                            aria-label="Limpar busca"
                        >
                            <X className="h-4 w-4 text-gray-400 hover:text-gray-600" />
                        </button>
                    )}
                </div>
            </div>

            {/* Mensagem de Erro */}
            {error && (
                <div className="mt-2">
                    <Alert type="error" message={error} />
                </div>
            )}

            {/* Dropdown de Resultados */}
            {isOpen && (
                <div
                    ref={resultsRef}
                    className="absolute z-50 w-full mt-2 bg-white border border-gray-200 rounded-lg shadow-lg max-h-96 overflow-y-auto min-w-0"
                >
                    {/* Histórico (quando não há query) */}
                    {!searchQuery && searchHistory.length > 0 && (
                        <div className="p-3 border-b border-gray-200">
                            <div className="flex items-center gap-2 text-xs font-semibold text-gray-500 mb-2">
                                <Clock className="h-4 w-4" />
                                <span>Buscas Recentes</span>
                            </div>
                            <div className="space-y-1">
                                {searchHistory.slice(0, 5).map((item, index) => (
                                    <button
                                        key={index}
                                        onClick={() => handleHistoryClick(item)}
                                        className="w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 rounded transition-colors"
                                    >
                                        {item}
                                    </button>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Resultados da Busca */}
                    {searchQuery && (
                        <>
                            {/* Header com contador */}
                            <div className="sticky top-0 bg-gray-50 px-4 py-2 border-b border-gray-200 flex items-center justify-between">
                                <span className="text-sm font-semibold text-gray-700">
                                    {totalResults} resultado{totalResults !== 1 ? 's' : ''} encontrado{totalResults !== 1 ? 's' : ''}
                                </span>
                                {isLoading && (
                                    <Loader2 className="h-4 w-4 text-emerald-500 animate-spin" />
                                )}
                            </div>

                            {/* Lista de Resultados */}
                            {totalResults > 0 ? (
                                <div className="p-2">
                                    {/* Paciente */}
                                    {groupedResults.paciente.length > 0 && (
                                        <ResultSection
                                            title="Dados do Paciente"
                                            results={groupedResults.paciente}
                                            searchQuery={searchQuery}
                                            selectedIndex={selectedIndex}
                                            onResultClick={handleResultClick}
                                            startIndex={0}
                                        />
                                    )}

                                    {/* Registros Diários */}
                                    {groupedResults.registrosDiario.length > 0 && (
                                        <ResultSection
                                            title="Registros Diários"
                                            results={groupedResults.registrosDiario}
                                            searchQuery={searchQuery}
                                            selectedIndex={selectedIndex}
                                            onResultClick={handleResultClick}
                                            startIndex={groupedResults.paciente.length}
                                        />
                                    )}

                                    {/* Medicações */}
                                    {groupedResults.medicacoes.length > 0 && (
                                        <ResultSection
                                            title="Medicações"
                                            results={groupedResults.medicacoes}
                                            searchQuery={searchQuery}
                                            selectedIndex={selectedIndex}
                                            onResultClick={handleResultClick}
                                            startIndex={
                                                groupedResults.paciente.length +
                                                groupedResults.registrosDiario.length
                                            }
                                        />
                                    )}

                                    {/* Equipamentos */}
                                    {groupedResults.equipamentos.length > 0 && (
                                        <ResultSection
                                            title="Equipamentos"
                                            results={groupedResults.equipamentos}
                                            searchQuery={searchQuery}
                                            selectedIndex={selectedIndex}
                                            onResultClick={handleResultClick}
                                            startIndex={
                                                groupedResults.paciente.length +
                                                groupedResults.registrosDiario.length +
                                                groupedResults.medicacoes.length
                                            }
                                        />
                                    )}

                                    {/* Tarefas */}
                                    {groupedResults.tarefas.length > 0 && (
                                        <ResultSection
                                            title="Tarefas"
                                            results={groupedResults.tarefas}
                                            searchQuery={searchQuery}
                                            selectedIndex={selectedIndex}
                                            onResultClick={handleResultClick}
                                            startIndex={
                                                groupedResults.paciente.length +
                                                groupedResults.registrosDiario.length +
                                                groupedResults.medicacoes.length +
                                                groupedResults.equipamentos.length
                                            }
                                        />
                                    )}
                                </div>
                            ) : (
                                <div className="p-8 text-center text-gray-500">
                                    <p className="text-sm">Nenhum resultado encontrado para "{searchQuery}"</p>
                                </div>
                            )}
                        </>
                    )}
                </div>
            )}
        </div>
    );
};

// Componente de Seção de Resultados
const ResultSection = ({ 
    title, 
    results, 
    searchQuery, 
    selectedIndex, 
    onResultClick,
    startIndex 
}) => {
    const highlightText = (text, query) => {
        if (!text || !query) return text;
        const parts = text.split(new RegExp(`(${query})`, 'gi'));
        return parts.map((part, index) => 
            part.toLowerCase() === query.toLowerCase() ? (
                <mark key={index} className="bg-yellow-200 font-semibold">{part}</mark>
            ) : (
                part
            )
        );
    };

    return (
        <div className="mb-4">
            <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wide px-2 mb-2">
                {title}
            </h4>
            <div className="space-y-1">
                {results.map((result, index) => {
                    const globalIndex = startIndex + index;
                    const isSelected = selectedIndex === globalIndex;
                    
                    return (
                        <button
                            key={result.id || index}
                            data-result-index={globalIndex}
                            onClick={() => onResultClick(result)}
                            className={`
                                w-full text-left px-3 py-2 rounded transition-colors
                                ${isSelected 
                                    ? 'bg-emerald-50 border-l-4 border-emerald-500' 
                                    : 'hover:bg-gray-50 border-l-4 border-transparent'
                                }
                            `}
                        >
                            <div className="flex items-start justify-between gap-2">
                                <div className="flex-1 min-w-0">
                                    <p className="text-sm font-medium text-gray-900 truncate">
                                        {highlightText(result.titulo, searchQuery)}
                                    </p>
                                    {result.descricao && (
                                        <p className="text-xs text-gray-500 mt-1 line-clamp-2">
                                            {highlightText(result.descricao, searchQuery)}
                                        </p>
                                    )}
                                    <div className="flex items-center gap-2 mt-1 text-xs text-gray-400">
                                        {result.categoria && (
                                            <span>{result.categoria}</span>
                                        )}
                                        {result.data_registro && (
                                            <span>• {new Date(result.data_registro).toLocaleDateString('pt-BR')}</span>
                                        )}
                                        {result.usuario_nome && (
                                            <span>• {result.usuario_nome}</span>
                                        )}
                                    </div>
                                </div>
                                {isSelected && (
                                    <ArrowUp className="h-4 w-4 text-emerald-500 flex-shrink-0" />
                                )}
                            </div>
                        </button>
                    );
                })}
            </div>
        </div>
    );
};

export default SearchBar;

